import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { Grid, Box, Alert, Paper, Typography } from '@mui/material';
import ChatInterface, { Message } from '../../common/ChatInterface';
import StepHeader from '../../common/StepHeader';
import DesignSpecEditor, { SectionFeedback } from './DesignSpecEditor';
import {
  useWorkflow,
  type ChatHistoryMessage,
} from '../../../contexts/WorkflowContext';
import {
  workflowService,
  type ChatResponse,
  type QualityReport,
  type DesignSpec,
  type StepStructuredData,
} from '../../../services/workflow';
import type {
  FeedbackRating,
  MessageFeedbackState,
} from '../../../types/feedback';

// Props for step data loaded from backend (for resume functionality)
interface DesignStepProps {
  initialChatHistory?: ChatHistoryMessage[];
  initialSpecData?: StepStructuredData | null;
  initialQualityScore?: number;
  initialQualityReport?: QualityReport;
}

// Default welcome message
const WELCOME_MESSAGE: Message = {
  id: '1',
  role: 'assistant',
  content:
    "Let's design your agent! I'll create an initial design specification based on your problem statement. One moment while I generate a draft...",
  timestamp: Date.now(),
};

const DesignStep: React.FC<DesignStepProps> = ({
  initialChatHistory,
  initialSpecData,
  initialQualityScore,
  initialQualityReport,
}) => {
  const { projectId, learningMode } = useWorkflow();

  // Track which messages have received feedback
  const [messageFeedback, setMessageFeedback] = useState<MessageFeedbackState>(
    {}
  );

  const handleFeedbackSubmit = useCallback(
    (messageIndex: number, rating: FeedbackRating) => {
      setMessageFeedback((prev) => ({
        ...prev,
        [messageIndex]: rating,
      }));
    },
    []
  );

  // Check if we have loaded data from a previous session
  const hasLoadedData = Boolean(
    initialChatHistory && initialChatHistory.length > 0
  );

  // Convert loaded chat history to Message format, or use welcome message
  const initialMessages = useMemo<Message[]>(() => {
    if (initialChatHistory && initialChatHistory.length > 0) {
      return initialChatHistory.map((msg, index) => ({
        id: `loaded-${index}`,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp).getTime(),
      }));
    }
    return [WELCOME_MESSAGE];
  }, [initialChatHistory]);

  const [messages, setMessages] = useState<Message[]>(initialMessages);

  // Design spec state - initialize from loaded data
  const [designSpec, setDesignSpec] = useState<DesignSpec | null>(() => {
    if (initialSpecData && 'agent_name' in initialSpecData) {
      return initialSpecData as DesignSpec;
    }
    return null;
  });
  const [sectionFeedback, setSectionFeedback] = useState<SectionFeedback[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Quality state - initialize from loaded data
  const [qualityScore, setQualityScore] = useState<number>(
    initialQualityScore ?? 0
  );
  const [qualityReport, setQualityReport] = useState<QualityReport | null>(
    initialQualityReport ?? null
  );

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialMessageSent = useRef(hasLoadedData); // Skip auto-trigger if we have loaded data

  // Update state when initial data changes
  useEffect(() => {
    if (initialChatHistory && initialChatHistory.length > 0) {
      setMessages(
        initialChatHistory.map((msg, index) => ({
          id: `loaded-${index}`,
          role: msg.role,
          content: msg.content,
          timestamp: new Date(msg.timestamp).getTime(),
        }))
      );
      initialMessageSent.current = true; // Don't auto-trigger if we loaded history
    }
  }, [initialChatHistory]);

  useEffect(() => {
    if (initialSpecData && 'agent_name' in initialSpecData) {
      setDesignSpec(initialSpecData as DesignSpec);
    }
  }, [initialSpecData]);

  useEffect(() => {
    if (initialQualityScore !== undefined) {
      setQualityScore(initialQualityScore);
    }
  }, [initialQualityScore]);

  useEffect(() => {
    if (initialQualityReport) {
      setQualityReport(initialQualityReport);
    }
  }, [initialQualityReport]);

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!projectId) {
        setError(
          'No project selected. Please create or select a project first.'
        );
        return;
      }

      const newMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, newMessage]);
      setIsLoading(true);
      setError(null);

      try {
        const response: ChatResponse = await workflowService.chat(
          projectId,
          content,
          learningMode
        );

        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.message,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, assistantMessage]);

        // Update design spec from structured_data if present (type guard for DesignSpec)
        if (
          response.structured_data &&
          'agent_name' in response.structured_data
        ) {
          const spec = response.structured_data as DesignSpec;
          setDesignSpec(spec);
        }

        // Extract section feedback from quality report issues
        // Map issues to sections based on keywords
        if (response.quality_report?.issues) {
          const sectionKeywords: Record<string, string[]> = {
            agent_name: ['name', 'title'],
            persona: ['persona', 'personality'],
            tone: ['tone', 'communication'],
            primary_goal: ['goal', 'objective', 'primary'],
            secondary_goals: ['secondary', 'additional goals'],
            capabilities: ['capabilities', 'can do', 'features'],
            boundaries: ['boundaries', 'limitations', 'should not'],
            autonomy_level: ['autonomy', 'independence'],
            error_handling: ['error', 'handling', 'edge case'],
          };

          const feedback: SectionFeedback[] = Object.entries(
            sectionKeywords
          ).map(([section, keywords]) => {
            const matchingIssue = response.quality_report?.issues?.find(
              (issue) => keywords.some((kw) => issue.toLowerCase().includes(kw))
            );
            return {
              section,
              score: matchingIssue ? 0.5 : 0.8,
              suggestion: matchingIssue || '',
              is_weak: !!matchingIssue,
            };
          });
          setSectionFeedback(feedback);
        }

        // Update quality score
        if (response.quality_score !== undefined) {
          setQualityScore(response.quality_score);
        }

        // Update quality report
        if (response.quality_report) {
          setQualityReport(response.quality_report);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to send message';
        setError(errorMessage);
        console.error('Chat error:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [projectId, learningMode]
  );

  // Handle spec save from editor
  const handleSpecSave = useCallback(async (spec: DesignSpec) => {
    setIsSaving(true);
    try {
      // Update local state with the saved spec
      setDesignSpec(spec);
      // TODO: Optionally send to backend if we want to persist edits
    } finally {
      setIsSaving(false);
    }
  }, []);

  // Handle ask about section - send message to agent
  const handleAskAboutSection = useCallback(
    (section: string, prompt: string) => {
      handleSendMessage(`${prompt} (focusing on the ${section} section)`);
    },
    [handleSendMessage]
  );

  // Send initial message to trigger draft generation when component mounts
  useEffect(() => {
    if (projectId && !initialMessageSent.current) {
      initialMessageSent.current = true;
      // Send a trigger message to the agent to generate the initial draft
      handleSendMessage(
        'Please generate an initial design specification based on my problem statement.'
      );
    }
  }, [projectId, handleSendMessage]);

  // Calculate quality score as percentage
  const qualityPercent = Math.round(qualityScore * 100);

  return (
    <>
      <StepHeader step="design" />
      <Grid container spacing={2} sx={{ height: '100%' }}>
        {/* Left Column: Chat */}
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          <ChatInterface
            messages={messages}
            onSendMessage={handleSendMessage}
            isLoading={isLoading}
            height="calc(100vh - 250px)"
            enableFeedback={!!projectId}
            projectId={projectId || undefined}
            step="design"
            messageFeedback={messageFeedback}
            onFeedbackSubmit={handleFeedbackSubmit}
          />
        </Grid>

        {/* Right Column: Design Specification */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              height: 'calc(100vh - 250px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {/* Spec editor */}
            <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
              <DesignSpecEditor
                spec={designSpec}
                onSave={handleSpecSave}
                onAskAboutSection={handleAskAboutSection}
                sectionFeedback={sectionFeedback}
                qualityScore={qualityScore}
                qualityScoreLabel="Completeness"
                isLoading={isLoading}
                isSaving={isSaving}
              />
            </Box>

            {/* Quality indicator - styled based on quality score */}
            <Paper
              sx={{
                p: 2,
                flexShrink: 0,
                bgcolor:
                  qualityPercent >= 80
                    ? 'success.light'
                    : qualityPercent >= 50
                      ? 'warning.light'
                      : 'grey.100',
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {qualityPercent >= 80
                  ? 'Your design specification is ready for multi-agent planning.'
                  : qualityPercent >= 50
                    ? 'Your design is taking shape. You can proceed or continue refining.'
                    : designSpec
                      ? 'Keep iterating to strengthen your design, or proceed when ready.'
                      : 'Chat with the agent to generate a design specification.'}
              </Typography>
              {qualityReport &&
                qualityReport.issues &&
                qualityReport.issues.length > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
                  >
                    Suggestions: {qualityReport.issues.slice(0, 2).join(', ')}
                  </Typography>
                )}
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default DesignStep;
